<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAdminMail } from '@/composables/useMail'
import { handleAxiosError } from '@/composables/useAxiosError'
import { formatNumber, formatBytes } from '@/utils'
import BaseChart from '@/components/base/BaseChart.vue'
import type { MailServerHealth, MailServerStats, MailQueueStatus, MessageStatsResponse, AccountsSummaryResponse } from '@/types/dashboard/mail'

const { t } = useI18n()
const router = useRouter()
const { getHealth, getStats, getQueue, getMessageStats, getAccountsSummary } = useAdminMail()

const loading = ref(true)
const health = ref<MailServerHealth | null>(null)
const stats = ref<MailServerStats | null>(null)
const queue = ref<MailQueueStatus | null>(null)
const msgStats = ref<MessageStatsResponse | null>(null)
const accountsSummary = ref<AccountsSummaryResponse | null>(null)
const chartPeriod = ref<'day' | 'week' | 'month'>('day')

const healthStatus = computed(() => {
  if (!health.value) return 'unknown'
  if (health.value.stalwart_reachable && health.value.queue_healthy) return 'healthy'
  if (health.value.stalwart_reachable || health.value.queue_healthy) return 'degraded'
  return 'unhealthy'
})

const healthColor = computed(() => {
  switch (healthStatus.value) {
    case 'healthy':
      return 'success'
    case 'degraded':
      return 'warning'
    case 'unhealthy':
      return 'error'
    default:
      return 'grey'
  }
})

const fetchData = async () => {
  loading.value = true
  try {
    const [healthRes, statsRes, queueRes, msgStatsRes, summaryRes] = await Promise.all([
      getHealth(),
      getStats(),
      getQueue(),
      getMessageStats(chartPeriod.value),
      getAccountsSummary()
    ])
    if (healthRes.data) health.value = healthRes.data
    if (statsRes.data) stats.value = statsRes.data
    if (queueRes.data) queue.value = queueRes.data
    if (msgStatsRes.data) msgStats.value = msgStatsRes.data
    if (summaryRes.data) accountsSummary.value = summaryRes.data
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AdminMailHome' })
  } finally {
    loading.value = false
  }
}

const navigateTo = (name: string) => {
  router.push({ name })
}

const storagePercent = computed(() => {
  if (!accountsSummary.value || !accountsSummary.value.total_quota_bytes) return 0
  return Math.round((accountsSummary.value.total_used_bytes / accountsSummary.value.total_quota_bytes) * 100)
})

const chartLabels = computed(() => {
  if (!msgStats.value) return []
  return msgStats.value.timeline.map((p) => {
    const d = new Date(p.timestamp)
    if (chartPeriod.value === 'day') return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })
  })
})

const chartDatasets = computed(() => {
  if (!msgStats.value) return []
  const tl = msgStats.value.timeline
  return [
    { label: 'Inbound', data: tl.map((p) => p.inbound), borderColor: '#64b5f6', backgroundColor: 'rgba(100, 181, 246, 0.1)', fill: true },
    { label: 'Outbound', data: tl.map((p) => p.outbound), borderColor: '#81c784', backgroundColor: 'rgba(129, 199, 132, 0.1)', fill: true },
    { label: 'Spam', data: tl.map((p) => p.spam), borderColor: '#ef5350', backgroundColor: 'rgba(239, 83, 80, 0.1)', fill: true }
  ]
})

const switchPeriod = async (p: 'day' | 'week' | 'month') => {
  chartPeriod.value = p
  try {
    const res = await getMessageStats(p)
    if (res.data) msgStats.value = res.data
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AdminMailHome' })
  }
}

onMounted(() => {
  fetchData()
})
</script>

<template>
  <div class="admin-mail-home">
    <div class="page-header">
      <h1>{{ t('dashboard.mail.admin.title') }}</h1>
      <div class="header-actions">
        <button class="refresh-btn" :disabled="loading" @click="fetchData">
          <v-icon icon="mdi-refresh" size="18" :class="{ spinning: loading }" />
          <span>{{ t('common.refresh') }}</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading && !health" class="loading-state">
      <v-progress-circular indeterminate size="48" width="3" color="primary" />
    </div>

    <template v-else>
      <!-- Accounts Summary -->
      <div v-if="accountsSummary" class="summary-card">
        <div class="summary-row">
          <div class="summary-stat">
            <span class="summary-value">{{ accountsSummary.total_accounts }}</span>
            <span class="summary-label">{{ t('dashboard.mail.admin.dashboard.totalAccounts') }}</span>
          </div>
          <div class="summary-stat">
            <span class="summary-value">{{ accountsSummary.active_accounts }}</span>
            <span class="summary-label">{{ t('dashboard.mail.admin.dashboard.activeAccounts') }}</span>
          </div>
          <div class="summary-stat">
            <span class="summary-value">{{ formatBytes(accountsSummary.total_used_bytes) }} / {{ formatBytes(accountsSummary.total_quota_bytes) }}</span>
            <span class="summary-label">{{ t('dashboard.mail.admin.dashboard.storage') }} ({{ storagePercent }}%)</span>
          </div>
        </div>
        <div class="type-chips">
          <span v-for="(count, type) in accountsSummary.by_type" :key="type" class="type-chip">
            {{ count }} {{ type }}
          </span>
        </div>
      </div>

      <!-- Quick Nav -->
      <div class="quick-nav">
        <button class="nav-card" @click="navigateTo('dashboardAdminMailAccounts')">
          <v-icon icon="mdi-account-multiple" size="24" />
          <span>{{ t('dashboard.mail.admin.nav.accounts') }}</span>
        </button>
        <button class="nav-card" @click="navigateTo('dashboardAdminMailMessages')">
          <v-icon icon="mdi-email-search" size="24" />
          <span>{{ t('dashboard.mail.admin.nav.messages') }}</span>
        </button>
        <button class="nav-card" @click="navigateTo('dashboardAdminMailServer')">
          <v-icon icon="mdi-server" size="24" />
          <span>{{ t('dashboard.mail.admin.nav.server') }}</span>
        </button>
        <button class="nav-card" @click="navigateTo('dashboardAdminMailSendLimits')">
          <v-icon icon="mdi-speedometer-slow" size="24" />
          <span>{{ t('dashboard.mail.admin.nav.sendLimits') }}</span>
        </button>
      </div>

      <!-- Health Status -->
      <div class="health-card">
        <div class="card-header">
          <h3>{{ t('dashboard.mail.admin.overview.health.title') }}</h3>
          <v-chip :color="healthColor" size="small" variant="tonal">
            {{ t(`dashboard.mail.admin.overview.health.${healthStatus}`) }}
          </v-chip>
        </div>
        <div v-if="health" class="health-grid">
          <div class="health-item">
            <v-icon
              :icon="health.stalwart_reachable ? 'mdi-check-circle' : 'mdi-alert-circle'"
              :color="health.stalwart_reachable ? 'success' : 'error'"
              size="20"
            />
            <span>{{ t('dashboard.mail.admin.server.health.stalwart') }}</span>
          </div>
          <div class="health-item">
            <v-icon
              :icon="health.queue_healthy ? 'mdi-check-circle' : 'mdi-alert-circle'"
              :color="health.queue_healthy ? 'success' : 'error'"
              size="20"
            />
            <span>{{ t('dashboard.mail.admin.server.health.queue') }}</span>
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div v-if="stats" class="stats-grid">
        <div class="stat-card">
          <div class="stat-icon">
            <v-icon icon="mdi-email-arrow-left" size="24" />
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ formatNumber(stats.messages_in_total) }}</span>
            <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.messagesIn') }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon">
            <v-icon icon="mdi-email-arrow-right" size="24" />
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ formatNumber(stats.messages_out_total) }}</span>
            <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.messagesOut') }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon warning">
            <v-icon icon="mdi-email-off" size="24" />
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ formatNumber(stats.spam_rejected_total) }}</span>
            <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.spamRejected') }}</span>
          </div>
        </div>
        <div class="stat-card">
          <div class="stat-icon danger">
            <v-icon icon="mdi-shield-alert" size="24" />
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ formatNumber(stats.auth_failures_total) }}</span>
            <span class="stat-label">{{ t('dashboard.mail.admin.overview.stats.authFailures') }}</span>
          </div>
        </div>
      </div>

      <!-- Message Flow Chart -->
      <div v-if="msgStats" class="flow-card">
        <div class="card-header">
          <h3>{{ t('dashboard.mail.admin.dashboard.messageFlow') }}</h3>
          <div class="period-selector">
            <button v-for="p in (['day', 'week', 'month'] as const)" :key="p" :class="{ active: chartPeriod === p }" @click="switchPeriod(p)">
              {{ t(`dashboard.mail.admin.dashboard.period.${p}`) }}
            </button>
          </div>
        </div>
        <BaseChart
          type="line"
          :labels="chartLabels"
          :datasets="chartDatasets"
          :height="200"
        />
        <div class="flow-summary">
          <span>{{ t('dashboard.mail.admin.dashboard.spamRate') }}: <strong :style="{ color: msgStats.spam_rate > 10 ? '#ef5350' : '#81c784' }">{{ msgStats.spam_rate.toFixed(1) }}%</strong></span>
        </div>
      </div>

      <!-- Queue Status -->
      <div class="queue-card">
        <div class="card-header">
          <h3>{{ t('dashboard.mail.admin.overview.queue.title') }}</h3>
          <v-chip color="blue" size="small" variant="tonal">
            {{ queue?.pending ?? 0 }} {{ t('dashboard.mail.admin.overview.queue.pending') }}
          </v-chip>
        </div>
        <div v-if="queue?.messages && queue.messages.length > 0" class="queue-preview">
          <div v-for="(msg, idx) in queue.messages.slice(0, 3)" :key="idx" class="queue-item">
            <span class="queue-from">{{ msg.from }}</span>
            <v-icon icon="mdi-arrow-right" size="14" />
            <span class="queue-to">{{ Array.isArray(msg.to) ? msg.to.join(', ') : msg.to }}</span>
            <span class="queue-retries">{{ msg.retry_count }}x</span>
          </div>
          <button v-if="queue.messages.length > 3" class="view-all-btn" @click="navigateTo('dashboardAdminMailServer')">
            {{ t('common.viewAll') }} ({{ queue.messages.length }})
          </button>
        </div>
        <div v-else class="queue-empty">
          <v-icon icon="mdi-check-circle" size="20" color="success" />
          <span>{{ t('dashboard.mail.admin.overview.queue.empty') }}</span>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.admin-mail-home {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;

  h1 {
    font-size: 1.5rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.2);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px;
}

.quick-nav {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.nav-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  color: rgba(200, 218, 255, 0.8);
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.1);
    border-color: rgba(120, 163, 255, 0.2);
    color: rgba(200, 218, 255, 1);
  }

  span {
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.summary-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.summary-row {
  display: flex;
  gap: 32px;
  margin-bottom: 12px;
}

.summary-stat {
  display: flex;
  flex-direction: column;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.summary-label {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.5);
}

.type-chips {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.type-chip {
  padding: 3px 10px;
  background: rgba(120, 163, 255, 0.08);
  border-radius: 4px;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.6);
}

.flow-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.period-selector {
  display: flex;
  gap: 4px;

  button {
    padding: 4px 12px;
    background: rgba(120, 163, 255, 0.05);
    border: 1px solid rgba(120, 163, 255, 0.1);
    border-radius: 6px;
    color: rgba(200, 218, 255, 0.5);
    font-size: 0.75rem;
    cursor: pointer;

    &.active {
      background: rgba(120, 163, 255, 0.15);
      border-color: rgba(120, 163, 255, 0.3);
      color: rgba(200, 218, 255, 0.95);
    }

    &:hover:not(.active) { background: rgba(120, 163, 255, 0.1); }
  }
}

.flow-summary {
  margin-top: 12px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.health-card,
.queue-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    margin: 0;
  }
}

.health-grid {
  display: flex;
  gap: 24px;
}

.health-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.9rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 16px;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: rgba(100, 181, 246, 0.2);
  border-radius: 12px;
  color: #90caf9;

  &.warning {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.danger {
    background: rgba(244, 67, 54, 0.15);
    color: #ef5350;
  }
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.6);
}

.queue-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.queue-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(120, 163, 255, 0.05);
  border-radius: 6px;
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.8);
}

.queue-from,
.queue-to {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.queue-retries {
  margin-left: auto;
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.view-all-btn {
  align-self: flex-start;
  margin-top: 8px;
  padding: 6px 12px;
  background: transparent;
  border: none;
  color: #64b5f6;
  font-size: 0.85rem;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.queue-empty {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(200, 218, 255, 0.6);
  font-size: 0.9rem;
}

/* Light Theme */
.v-theme--lightTheme {
  .page-header h1 {
    color: rgba(15, 23, 42, 0.95);
  }

  .summary-card,
  .flow-card,
  .nav-card,
  .health-card,
  .queue-card,
  .stat-card {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .card-header h3 {
    color: rgba(15, 23, 42, 0.95);
  }

  .stat-value {
    color: rgba(15, 23, 42, 0.95);
  }

  .health-item,
  .queue-item {
    color: rgba(15, 23, 42, 0.8);
  }
}
</style>
